import {Component, ElementRef, inject, OnDestroy, OnInit, ViewChild, viewChildren} from '@angular/core';
import {RightPanel} from '../right-panel.class';
import {ActivatedRoute, Router} from '@angular/router';
import {ConfigurationService} from '../../../../../configuration/configuration.service';
import {AssetsManagerService} from '../../../../../data/assets/assets-manager.service';
import {ObservationsManagerService} from '../../../../../data/observations/observations-manager.service';
import {Observation} from '../../../../../shared/models/observation';
import {ActionMenuItem, ActionMenuItemSubMenu} from '../../../../../shared/models/action-menu-item.class';
import {ShortDateOrTimePipe} from '../../../../../shared/formatting/short-date-or-time.pipe';
import {SecurityService} from '../../../../../security/security.service';
import {ObservationsService} from '../../../../../data/observations/observations.service';
import {DrawerContent} from '../../../../../layouts/right-drawer/right-drawer.component';
import {DateFilter} from 'src/app/shared/models/DateFilter';
import {MultiSelectFilter} from '../../../../../shared/components/multi-select-component';
import {FilterBarComponent} from '../../../../../shared/components/filter-bar/filter-bar.component';
import {FilterButtonComponent} from '../../../../../shared/components/buttons/filter-button/filter-button.component';
import {CardMenuItemComponent} from '../../../../../shared/components/card-menu-item/card-menu-item.component';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { NewObservationComponent } from './new-observation/new-observation.component';
import { EditObservationComponent } from './edit-observation/edit-observation.component';
import { ObservationModel } from '../../../../../shared/models/observation.model';

@Component({
  selector: 'app-observations',
  templateUrl: './observations.component.html',
  styleUrls: ['./observations.component.scss'],
  imports: [
    FilterButtonComponent,
    FilterBarComponent,
    CardMenuItemComponent,
    MatIcon,
    MatButton,
    NewObservationComponent,
    EditObservationComponent,
  ],
  standalone: true,
})
export class ObservationsComponent extends RightPanel implements OnInit, OnDestroy {
  private securityService = inject(SecurityService);
  private observationsManager = inject(ObservationsManagerService);
  private observationsService = inject(ObservationsService);
  private shortDateOrTimePipe = inject(ShortDateOrTimePipe);

  initialAction = false;

  newObservation: any;
  editObservation: Observation;
  selectedObservationId: number;
  allObservations: Observation[];
  items: ActionMenuItem[];
  isAdmin = false;

  @ViewChild(FilterBarComponent) filterBar: FilterBarComponent;

  // Signal-based viewChildren query returns a Signal containing an array of ElementRefs
  private itemRefs = viewChildren('itemRef', { read: ElementRef });

  constructor(
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected configurationService: ConfigurationService,
    protected assetsManager: AssetsManagerService
  ) {
    super(DrawerContent.OBSERVATION, router, activatedRoute, configurationService, assetsManager);
  }

  ngOnInit(): void {
    this.isAdmin = this.securityService.isAdminSync();
    this.initialize();
    this.loadConfiguration();

    const observationSubscription = this.observationsManager.filteredObservations$.subscribe(allObservations => {
      this.allObservations = allObservations;
      if (this.initialAction) {
        this.onAssetAction();
        this.initialAction = false;
      }
      this.observationsToItems();
    });
    this.openSubscriptions.push(observationSubscription);

    // when clicked in live-map, select it and scroll to it
    this.openSubscriptions.push(
      this.observationsManager.highlightedObservation$.subscribe(highlightedObservationId => {
        if (highlightedObservationId?.observation?.id !== null) {
          const observation = this.allObservations.find(observation => observation.id === highlightedObservationId?.observation?.id);
          if (observation) {
            this.selectedObservationId = observation.id;
            this.selectAndScroll(observation.id);
          }
        } else {
          this.selectedObservationId = null;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe();
    this.observationsManager.hideAll();
    this.items = undefined;
  }

  onAssetsUpdate() {
    // do nothing
  }

  onAssetChange() {
    this.selectedObservationId = null;

    // do not filter observations at all
    this.observationsManager.filterByShiftIds(null);
  }

  onAssetAction() {
    if (!!this.allObservations) {
      if (this.asset) {
        const vehicle = this.assetsManager.getVehicles().find(v => v.id === this.asset.id);
        if (this.filterBar) {
          this.filterBar.overrideVehicleFilter([vehicle]);
        } else {
          setTimeout(() => this.filterBar?.overrideVehicleFilter([vehicle]), 1000);
        }
      }
    } else {
      this.initialAction = true;
    }
  }

  onRouteAction() {
    // do nothing
  }

  onConfigurationLoad() {
    // do nothing
  }

  private observationsToItems() {
    if (!!this.allObservations) {
      const filteredObservations = this.allObservations
        .sort((a, b) => {
          return +new Date(b.created) - +new Date(a.created);
        });
      this.items = filteredObservations.map(observation => this.observationToActionMenuItem(observation));
    }
  }

  private selectObservation(observation: Observation) {
    this.selectedObservationId = observation.id;
    this.observationsManager.highlightObservation(observation.id, ObservationsManagerService.LIST_ACTION_SOURCE);
  }

  /*private viewObservation(observation: Observation) {
    this.observationsManager.highlightObservation(observation.id, ObservationsManagerService.LIST_ACTION_SOURCE);
    this.router.navigate(['/live-map', LiveMapTab.OBSERVATIONS], {
      queryParams: { id: observation.id, scrollToView: true },
    });
  }*/

  private deleteObservation(observationId: number) {
    this.observationsService
      .deleteObservation(observationId)
      .then(() => {
        console.warn(`Observation ${observationId} deleted!`);
      })
      .catch(error => {
        console.error('Observation deletion failed!');
        console.error(error);
      });
  }

  private viewShiftDetail(shiftId: number) {
    this.router.navigate(['/shift-detail', shiftId], {
      queryParams: {
        source: 'observations',
      },
    });
  }

  private observationToActionMenuItem(observation: Observation): ActionMenuItem {
    const actions: ActionMenuItemSubMenu[] = [];
    if (this.isAdmin) {
      actions.push(new ActionMenuItemSubMenu('edit', 'Edit', () => this.editObservation = observation));
      actions.push(new ActionMenuItemSubMenu('delete', 'Delete', () => this.deleteObservation(observation.id)));
    }
    // TODO fixme
    /*if (!observation.activeShift) {
      actions.push(
          new ActionMenuItemSubMenu(
              'map',
              'View Shift Detail',
              () => this.viewShiftDetail(observation.shiftId),
          )
      );
    }*/
    return new ActionMenuItem(
      observation.id,
      'stars',
      observation.observationType.title,
      observation.observationType.group?.name,
      this.shortDateOrTimePipe.transform(observation.created),
      null,
      null,
      () => this.selectedObservationId === observation.id,
      null,
      null,
      () => this.selectObservation(observation),
      null,
      null,
      actions
    );
  }

  onNewObservationClick() {
    // this.newObservation = // new Observation();
    this.newObservation = {};
  }

  onObservationUpdated(observation: ObservationModel) {
    this.editObservation = null;
  }

  onObservationCreated(observation: ObservationModel) {
    this.newObservation = null;
  }

  onUpdateCanceled() {
    this.editObservation = null;
  }

  onCreateCanceled() {
    this.newObservation = null;
  }

  onDateFilterChanged(dateFilter: DateFilter) {
    this.observationsManager.filterByDate(dateFilter);
  }

  onDriverFilterChanged(driverFilter: MultiSelectFilter<number>) {
    this.observationsManager.filterByDriver(driverFilter?.elements);
  }

  onVehicleFilterChanged(vehicleFilter: MultiSelectFilter<number>) {
    this.observationsManager.filterByVehicle(vehicleFilter?.elements);
  }

  onObservationTypeGroupFilterChanged(observationTypeGroupFilter: MultiSelectFilter<number>) {
    this.observationsManager.filterByObservationTypeGroup(observationTypeGroupFilter?.elements);
  }

  /**
   * Scrolls to the observation with the given ID and selects it.
   * @param id - The ID of the observation to scroll to and select.
   */
  selectAndScroll(id: number) {
    const elements = this.itemRefs();
    const targetElement = elements.find(el => el.nativeElement?.getAttribute('id') === `obs-${id}`);
    if (targetElement) {
      targetElement.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }
}
